<template>
    <div>
        <ToastNotification
            :message="toastMessage"
            :background-class="toastBackgroundClass"
            @closed="toastMessage = ''"
        />
        
        <h3 class="font-bold text-xl">{{ $t('invite_employees') }}</h3>

        <div class="mt-4">
            <MazSelect v-if="accountTypeList.length>0"
                class="mt-1"
                v-model="position_id"
                :placeholder="$t('select_position')"
                :options="accountTypeList"
                label="Position"
                optionValueKey="id"
                optionLabelKey="name"
                option-input-value-key="name"
            />
        </div>

        <div class="mt-4 flex gap-4">
            <div>
                <MazSelect
                    class="mt-1"
                    v-model="group_id"
                    :placeholder="$t('group') + '...'"
                    :options="[{ id: null, name: $t('all_groups') }, ...groups]"
                    :label="$t('group')"
                    optionValueKey="id"
                    optionLabelKey="name"
                    option-input-value-key="name"
                />
            </div>
            <div v-if="group_id != 0 && teams.length > 0">
                <MazSelect
                    class="mt-1"
                    v-model="team_id"
                    :placeholder="$t('team') + '...'"
                    :options="[{ id: null, name: $t('all_teams') }, ...teams]"
                    :label="$t('team')"
                    optionValueKey="id"
                    optionLabelKey="name"
                    option-input-value-key="name"
                />
            </div>
        </div>

        <div class="mt-4 w-max">
            <MazInputTags
                class="mt-1"
                v-model="emailTags"
                :placeholder="$t('add_emails') + '...'"
                :addOn="['Enter', ' ']"
                :addTagsOnBlur="true"
                removeOnBackspace
                />
            <br />
            <span class="mt-1 text-xs text-gray-700 dark:text-gray-300">*{{ $t('enter_to_confirm') }}</span>
        </div>
        <div v-if="invalidEmails.length > 0" class="text-decline text-sm">
            {{ $t('emails_invalid') }} {{ invalidEmails.join(', ') }}
        </div>
        <MazBtn class="clear-both mt-2" size="sm"
            :disabled="emailTags.length == 0 || invalidEmails.length > 0 || position_id == 0" 
            :class="{'bg-gray-300': emailTags.length == 0 || invalidEmails.length > 0  || position_id == 0}" 
            @click="inviteEmployees">
            {{ $t('send_invites') }}
        </MazBtn>

        <div v-if="issues.length > 0" class="mt-4 text-sm text-decline">
            <p>{{ $t('issues') }}:</p>
            <ul class="list-disc pl-5">
                <li v-for="(issue, index) in issues" :key="index">{{ issue.message }}</li>
            </ul>
        </div>

        <div class="mt-4 border-t border-sepBorder pt-4">
            <h3 class="font-bold text-xl mb-3">{{ $t('awaiting_invites') }}</h3>
            <div v-if="pendingInvites.length > 0">
                <div v-for="invite in pendingInvites" :key="invite.id" class="my-1.5 p-2 px-3 border border-sepBorder dark:border-dark.sepBorder w-full bg-panels dark:bg-dark.tableBG rounded-lg">
                    <div class="grid grid-cols-4 gap-4 items-center">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-200 font-semibold">{{ invite.email }}</p>
                            <p class="text-xs text-gray-700 dark:text-gray-200 capitalize">{{ invite.position_name.replace("_", " ") }}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-200 font-semibold">{{ $t('status') }}</p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ invite.status }}</p>
                        </div>
                        <div>
                            <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ $t('expiration') }}</p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ invite.expiration }}</p>
                        </div>
                        <div class="flex gap-2 whitespace-nowrap" v-if="invite.id > 0">
                            <MazBtn size="xs" class="h-2 px-2" @click="resendInvite(invite.id)">
                                {{ $t('resend_invite') }}
                            </MazBtn>
                            <MazBtn size="xs" color="danger" class="h-2 px-2" @click="cancelInvite(invite.id)">
                                {{ $t('cancel_invite') }}
                            </MazBtn>
                        </div>
                    </div>
                </div>
            </div>
            <p v-else class="text-sm text-gray-700">{{ $t('no_pending_invites') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { api_post_call, get_groups, get_pending_invites, invite_actions } from '@/api/api.js'
    import ToastNotification from '@/components/ToastNotification.vue';
</script>

<script>
export default {
    components: {
        ToastNotification,
    },
    data() {
        return {
            emailTags: [],
            invalidEmails: [],
            position_id: 0,
            group_id: 0,
            team_id: 0,
            accountTypeList: [],
            pendingInvites: [],
            groups: [],
            teams: [],
            issues: [],
            toastMessage: '',
            toastBackgroundClass: 'bg-green-500',
        }
    },
    methods: {
        async resendInvite(invite_id) {
            try {
                await invite_actions(this.$cookies.get('userToken'),this.$cookies.get('c_id'),"resend_invite", invite_id);
                this.toastMessage = 'Resent';
                this.toastBackgroundClass = 'bg-growth';
            } catch (error) {
                this.toastMessage = 'Error';
                this.toastBackgroundClass = 'bg-decline';
            }
        },
        async cancelInvite(invite_id) {
            let r = await invite_actions(this.$cookies.get('userToken'),this.$cookies.get('c_id'),"cancel_invite", invite_id);
            if (r.success) {
                this.pendingInvites = this.pendingInvites.filter(invite => invite.id !== invite_id);
                this.toastMessage = 'Canceled';
                this.toastBackgroundClass = 'bg-growth';
            } else {
                this.toastMessage = 'Error';
                this.toastBackgroundClass = 'bg-decline';
            }
        },
        validateEmail(value) {
            // Email validation regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            const isValid = emailRegex.test(value)
            
            // Keep track of invalid emails for user feedback
            if (!isValid) {
                this.invalidEmails.push(value)
            }

            return isValid
        },
        async getListData(whichData,group,team,mSummary = null, platform = null) {
            return new Promise(async (resolve, reject) => {
                try {
                    const result = await this.$axios.get("lists/", {
                        params: {
                            build: whichData,
                            groupFilter: group,
                            teamFilter: team,
                            msummary:mSummary,
                            userToken: this.$cookies.get('userToken'),
                            companyID: this.$cookies.get('c_id'),
                        }
                    })
                    resolve(result.data);
                } catch (error) {
                    reject(error);
                }
            });
        },
        async inviteEmployees() {
            try {
                this.issues = [];
                // Send the invites
                // Use this.emailTags and this.position_id to send the invites
                let response = await api_post_call('/users/', {
                    action: "invite_users",
                    emails: this.emailTags,
                    group_id: this.group_id,
                    team_id: this.team_id,
                    position_id: this.position_id,
                    userToken: this.$cookies.get('userToken'),
                    companyID: this.$cookies.get('c_id')
                });
                if (response.success) {
                    this.toastMessage = 'Invites sent successfully!';
                    this.toastBackgroundClass = 'bg-growth';
                    this.pendingInvites = await get_pending_invites(this.$cookies.get("userToken"),this.$cookies.get('c_id'));
                    this.issues = response.issues || [];
                    this.emailTags = [];
                    this.invalidEmails = [];
                } else {
                    this.$toast.show(this.$t('error_sending_invites'), { type: 'error' });
                }
            } catch (error) {
                console.error(error);
            }
        }
    },
    watch: {
        emailTags(newTags) {
            //use the validateEmail fuction to detect invalid emails
            this.invalidEmails = [];
            newTags.forEach((tag) => this.validateEmail(tag))
        },
        group_id() {
            if (this.group_id !== 0) {
                const selectedGroup = this.groups.find(group => group.id === this.group_id);
                if (selectedGroup && selectedGroup.hasChildren) {
                    this.teams = selectedGroup.children;
                } else {
                    this.teams = [];
                }
            } else {
                this.teams = [];
            }
        }
    },
    async created() {
        this.accountTypeList = await this.getListData('userTypes', null, null, null);
        this.groups = await get_groups(this.$cookies.get("userToken"),this.$cookies.get('c_id'));
        this.pendingInvites = await get_pending_invites(this.$cookies.get("userToken"),this.$cookies.get('c_id'));
    }
}
</script>